/**
 * GMA-1620 (CompassCare Web 5/14 — Clients) — group-home rollup cards.
 *
 * The Group Homes view header: one card per home (via the shared
 * clientsModel.rollUpByHome), worst-status first. Each card shows the home
 * name, a per-home status badge + dot, the resident count, and a plain-language
 * status note. Selecting a card reports its home id so the parent can scope the
 * roster to that home (toggle-style).
 *
 * Presentational + fail-safe. a11y: a labelled "Group homes" group of
 * pressed-state card buttons; the count and note are text, not icon-only.
 * Colors come from the single design-token set; `critical` homes pulse.
 */
import React from "react";
import PropTypes from "prop-types";
import SeverityBadge from "./SeverityBadge";
import { rollUpByHome } from "../../pages/web/clientsModel";
import { theme, severityToken } from "../../styles/designTokens";

const isNil = (x) => x === null || x === undefined;

function statusNote(status) {
  if (status === "normal") return "All residents stable";
  if (status === "critical") return "Critical alert active";
  return "Needs attention";
}

export default function GroupHomeCards({ clients, selectedHomeId, onSelectHome }) {
  const homes = rollUpByHome(clients);
  const interactive = typeof onSelectHome === "function";

  return (
    <div
      role="group"
      aria-label="Group homes"
      data-testid="group-home-cards"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: 10,
        fontFamily: theme.fontUI,
      }}
    >
      {homes.map((h, index) => {
        const token = severityToken(h.status);
        // `undefined` = no selection; a real homeId (which may be null for an
        // "Unknown home" rollup) can still be the selected one.
        const pressed = selectedHomeId !== undefined && h.homeId === selectedHomeId;
        return (
          <button
            key={isNil(h.homeId) ? `${h.home}-${index}` : h.homeId}
            type="button"
            data-testid="group-home-card"
            data-home-id={isNil(h.homeId) ? undefined : h.homeId}
            aria-pressed={pressed}
            disabled={!interactive}
            onClick={interactive ? () => onSelectHome(h.homeId) : undefined}
            style={{
              textAlign: "left",
              display: "grid",
              gap: 8,
              padding: "12px 14px",
              borderRadius: 12,
              border: `1px solid ${pressed ? token.color : theme.border}`,
              background: theme.surface,
              color: theme.textPrimary,
              cursor: interactive ? "pointer" : "default",
              fontFamily: theme.fontUI,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: token.color,
                  animation: h.status === "critical" ? "ccpulse 1.6s infinite" : "none",
                  flex: "none",
                }}
              />
              <span style={{ fontWeight: 600, fontSize: 14 }}>{h.home}</span>
              <span style={{ marginLeft: "auto" }}>
                <SeverityBadge level={h.status} showDot={false} />
              </span>
            </div>
            <div style={{ fontSize: 12, color: theme.textMuted }}>
              {h.count} {h.count === 1 ? "resident" : "residents"}
            </div>
            <div style={{ fontSize: 12, color: h.status === "normal" ? theme.textMuted : token.color }}>
              {statusNote(h.status)}
            </div>
          </button>
        );
      })}
    </div>
  );
}

GroupHomeCards.propTypes = {
  // Client roster; group homes are rolled up internally. Fail-safe for null.
  clients: PropTypes.array,
  selectedHomeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // (homeId) => void. Absent -> cards render disabled.
  onSelectHome: PropTypes.func,
};
